import { api } from '@organization/api'
import { useSuccessLogin } from '@organization/hooks'
import { MFAResponseSendOTP, WithCustomMFAToken } from '@organization/models'
import { paths } from '@organization/router'
import { Text } from '@shared/components'
import { getDeviceName, showApiErrors } from '@shared/utils'
import { useMutation } from '@tanstack/react-query'
import {
  App,
  Button,
  Card,
  Checkbox,
  Collapse,
  Flex,
  Form,
  Input,
  Spin,
  Typography
} from 'antd'
import { AxiosError } from 'axios'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { AuthWrapper, OrganizationLogo } from '../components'
import { useStyle } from '../components/AuthWrapper/styles'

type LocationState = MFAResponseSendOTP &
  WithCustomMFAToken & { __created_at?: Date }

export const ConfirmMFAOtpCode = () => {
  const { styles } = useStyle()
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const location = useLocation()
  const { message } = App.useApp()
  const { auth } = useSuccessLogin()

  const locationState = useMemo(
    () => location.state,
    [location]
  ) as LocationState

  const timePassed = useCallback(() => {
    if (!locationState?.__created_at)
      return { expired: true, formattedTime: '', canResend: false }

    const createdAt = dayjs(locationState.__created_at)
    const now = dayjs()

    const secondsPassed = now.diff(createdAt, 'second')
    const totalSeconds = Number(locationState?.expires_in) - secondsPassed
    const canResend = Number(locationState?.wait) - secondsPassed < 0

    if (totalSeconds <= 0)
      return { expired: true, formattedTime: '', canResend }

    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60

    const formattedTime = `${minutes}:${seconds.toString().padStart(2, '0')}`

    return { expired: false, formattedTime, canResend }
  }, [locationState])

  const [expirationTime, setExpirationTime] = useState(timePassed)

  useEffect(() => {
    if (!locationState) return

    const timer = setInterval(() => {
      const expirationTime = timePassed()

      if (expirationTime.expired) clearInterval(timer)
      setExpirationTime(expirationTime)
    }, 1000)

    return () => clearInterval(timer)
  }, [locationState, timePassed])

  useEffect(() => {
    if (locationState) return

    navigate(paths.auth.login, { replace: true })
  }, [locationState, navigate])

  useEffect(() => {
    if (expirationTime.expired)
      form.setFields([
        { name: 'otp_code', errors: [t('error.otp_expired')], value: '' }
      ])
    else form.resetFields(['otp_code'])
  }, [expirationTime.expired, form, t])

  const checkErrorCodes = (
    error: AxiosError<{ code: string; detail: string }>
  ) => {
    const code = error.response?.data?.code
    const detail = error.response?.data?.detail

    if (code === 'token_not_valid') {
      showApiErrors({ body: t('error.token_not_valid') })
      navigate(paths.auth.login, { replace: true })
      return
    } else if (code?.includes('otp') && detail) {
      showApiErrors({ response: { data: { otp_code: detail } } }, form)
      return
    }

    showApiErrors(error, form)
  }

  const { mutate: verifyOtp, isPending: isVerifyOtpPending } = useMutation({
    mutationFn: api.mfa.verifyOtp,
    onSuccess: auth,
    onError: checkErrorCodes
  })

  const { mutate: resendOtp, isPending: isResendOtpPending } = useMutation({
    mutationFn: api.mfa.sendOtp,
    onSuccess: (data) => {
      message.success(t('otp_code_sent_successfully'))
      navigate(
        { pathname: location.pathname },
        {
          replace: true,
          relative: 'path',
          state: {
            ...locationState,
            ...data,
            __created_at: new Date()
          }
        }
      )
    },
    onError: checkErrorCodes
  })

  const onSubmit = async () => {
    const values = await form.validateFields()
    const deviceName = getDeviceName()

    verifyOtp({
      ...values,
      device_name: deviceName,
      __mfaAccessToken: locationState.__mfaAccessToken
    })
  }

  return (
    <AuthWrapper>
      <Card className={styles.card}>
        <OrganizationLogo
          message={(name) =>
            `${t('auth.sign_in_header')} ${name} ${t('common.account')}`
          }
        />

        <Form
          form={form}
          layout="vertical"
          className={styles.form}
          requiredMark={false}
        >
          <Form.Item
            name="otp_code"
            label={t('form.item.code_otp')}
            rules={[{ required: true, message: t('form.message.code_otp') }]}
          >
            <Input.OTP
              disabled={expirationTime.expired}
              style={{ justifyContent: 'center', width: '100%' }}
              size="large"
              formatter={(str) => str.replace(/\D/g, '')}
            />
          </Form.Item>

          {!expirationTime.expired && (
            <Flex justify="end">
              <Text color="colorPrimary">
                {t('common.code_expired_in', {
                  time: expirationTime.formattedTime
                })}
              </Text>
            </Flex>
          )}

          <Flex align="baseline" justify="space-between">
            <Form.Item name="trusted_device" valuePropName="checked">
              <Checkbox>{t('form.item.keep_me_logged_in')}</Checkbox>
            </Form.Item>

            <Spin size="small" spinning={isResendOtpPending}>
              <Typography.Link
                disabled={!expirationTime.canResend}
                onClick={() =>
                  resendOtp({
                    __mfaAccessToken: locationState?.__mfaAccessToken
                  })
                }
              >
                {t('resend_otp_code')}
              </Typography.Link>
            </Spin>
          </Flex>

          <Flex gap={12} vertical>
            <Collapse
              size="small"
              items={[
                {
                  children: (
                    <Link to={paths.auth.backUpConfirm} state={locationState}>
                      <Button block type="primary">
                        {t('backup_code_mfa')}
                      </Button>
                    </Link>
                  ),
                  showArrow: false,
                  label: (
                    <Flex justify="center">
                      <Text strong>{t('more_options')}</Text>
                    </Flex>
                  )
                }
              ]}
            />

            <Button
              block
              type="primary"
              htmlType="submit"
              loading={isVerifyOtpPending}
              onClick={onSubmit}
            >
              {t('button.sign_in')}
            </Button>
          </Flex>
        </Form>
      </Card>
    </AuthWrapper>
  )
}
